import {
    Fragment,
    ReactNode,
    ReactElement,
    useState,
    useRef,
    useEffect,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Spinner from '../Spinner/Spinner';
import { cn } from '../../utils';

type DrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    header?: string | ReactElement;
    children?: ReactNode;
    className?: string | object;
    footer?: ReactElement;
    style?: object;
    closeIcon?: boolean;
    isLoading?: boolean;
    maskClosable?: boolean;
};

function Drawer({
    isOpen,
    onClose,
    header,
    children = null,
    className = '',
    footer,
    style,
    closeIcon = true,
    isLoading = false,
    maskClosable = true,
}: DrawerProps) {
    const footerRef = useRef<HTMLDivElement>(null);
    const [footerHeight, setFooterHeight] = useState(0);

    useEffect(() => {
        const updateFooterHeight = () => {
            if (footerRef.current) {
                setFooterHeight(footerRef.current.offsetHeight);
            }
        };

        const handleLoad = () => {
            requestAnimationFrame(updateFooterHeight);
        };

        handleLoad();
        window.addEventListener('resize', updateFooterHeight);

        return () => window.removeEventListener('resize', updateFooterHeight);
    }, [footer]);

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog
                as="div"
                data-testid="drawer"
                className="relative alli-z-50"
                onClose={maskClosable ? onClose : () => {}}>
                <Transition.Child
                    as={Fragment}
                    enter="alli-ease-in-out alli-duration-500"
                    enterFrom="alli-opacity-0"
                    enterTo="alli-opacity-100"
                    leave="alli-ease-in-out alli-duration-500"
                    leaveFrom="alli-opacity-100"
                    leaveTo="alli-opacity-0">
                    <div className="alli-fixed alli-inset-0 alli-bg-black alli-bg-opacity-30 alli-transition-opacity" />
                </Transition.Child>

                <div className="alli-fixed alli-inset-0 alli-overflow-hidden">
                    <div className="alli-absolute alli-inset-0 alli-overflow-hidden">
                        <div
                            className={cn(
                                'alli-pointer-events-none alli-fixed alli-inset-y-0 alli-right-0 alli-flex alli-w-96 alli-max-w-full alli-pl-10',
                                className,
                            )}
                            style={style}>
                            <Transition.Child
                                as={Fragment}
                                enter="alli-transform alli-transition alli-ease-in-out alli-duration-500 sm:alli-duration-700"
                                enterFrom="alli-translate-x-full"
                                enterTo="alli-translate-x-0"
                                leave="alli-transform alli-transition alli-ease-in-out alli-duration-500 sm:alli-duration-700"
                                leaveFrom="alli-translate-x-0"
                                leaveTo="alli-translate-x-full">
                                <Dialog.Panel className="alli-pointer-events-auto alli-relative alli-flex alli-h-full alli-w-full alli-transform alli-flex-col alli-transition alli-duration-500 alli-ease-in-out">
                                    <div className="alli-flex alli-h-full alli-flex-col alli-bg-white">
                                        <div
                                            className="alli-sticky alli-top-0 alli-z-10 alli-bg-white alli-p-3"
                                            data-testid="header">
                                            <div
                                                className={cn('alli-flex', {
                                                    'alli-justify-between':
                                                        header,
                                                    'alli-justify-end': !header,
                                                })}>
                                                {header && (
                                                    <Dialog.Title className="alli-text-blue-gray-750 alli-text-md alli-justify-start alli-font-medium">
                                                        {header}
                                                    </Dialog.Title>
                                                )}
                                                {closeIcon && (
                                                    <div
                                                        className="alli-flex alli-items-center alli-justify-end"
                                                        title="Close"
                                                        data-testid="close">
                                                        <button
                                                            type="button"
                                                            onClick={onClose}
                                                            className="alli-relative alli-rounded-md alli-text-gray-750 hover:alli-text-gray-700 focus:alli-outline-none">
                                                            <span className="alli-absolute alli--inset-2.5" />
                                                            <XMarkIcon
                                                                aria-hidden="true"
                                                                className="alli-h-6 alli-w-6"
                                                            />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className="alli-flex-grow alli-overflow-y-auto alli-pl-3 alli-pr-3"
                                            style={{
                                                paddingBottom: `${footerHeight}px`,
                                            }}>
                                            <div
                                                className={cn({
                                                    'alli-absolute alli-inset-0 alli-flex alli-items-center alli-justify-center alli-bg-white':
                                                        isLoading,
                                                    'alli-mb-5 alli-space-y-4':
                                                        !isLoading,
                                                })}
                                                data-testid="content">
                                                {isLoading ? (
                                                    <Spinner size="lg" />
                                                ) : (
                                                    children
                                                )}
                                            </div>
                                        </div>
                                        {footer && (
                                            <div
                                                ref={footerRef}
                                                className="alli-border-gray-200 alli-fixed alli-bottom-0 alli-left-0 alli-right-0 alli-z-10 alli-bg-white alli-p-6"
                                                data-testid="footer">
                                                {footer}
                                            </div>
                                        )}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default Drawer;
